import { useEffect, useState } from 'react';
import useToastContext from '@contexts/ToastContext/hook';
import { parseFilters } from '@utils/parseFilters';
import { PaymentConversionPeriod } from '@interfaces/reports/paymentConversion';

export type ReportFiltersInput = {
  botConfigIds: number[];
  startDate: Date;
  endDate: Date;
  clientTz: string;
  /**
   * O papel pedido como ESTREITAMENTO, e opcional desde a #360.
   *
   * Ausente é o modo "Tudo o que eu alcanço": o recorte sai do `scopeByProduct` que a `platform-api`
   * resolve, e é o único que fala de acesso por grant. Os DTOs da `report-api` validam este campo só
   * quando o mapa não vem.
   */
  userRoles?: string[];
};

/**
 * The fetch half of every one of these report screens, which is identical in all of them.
 *
 * Extracted rather than copied a third time. Three things here are easy to get wrong and were each
 * gotten wrong once already in this codebase:
 *
 * - **A superseded request must lose.** Changing communities or period fires a new request while the
 *   old one is in flight, and without the `active` flag the slower response wins whichever it is.
 * - **Deselecting every community must stop the spinner.** The guard returns before the request, so the
 *   `finally` that would clear `isLoading` never runs - the card would spin forever instead of showing
 *   its empty state. It is cleared explicitly.
 * - **A missing field must degrade to a number.** The API deploys separately from this screen, so a
 *   response predating a new field would put `undefined` through a formatter and blank a cell in a table
 *   of numbers. `normalise` fills every one in.
 */
export function useReportRequest<T>(
  filters: ReportFiltersInput,
  timeAggregationPeriod: PaymentConversionPeriod,
  request: (payload: any) => Promise<{ data?: T }>,
  empty: T,
  normalise: (payload: Partial<T>) => T,
  extraPayload: Record<string, unknown> = {},
) {
  const toast = useToastContext();
  const [data, setData] = useState<T>(empty);
  const [isLoading, setIsLoading] = useState(false);
  /**
   * A request that FAILED, as opposed to one that answered with nothing.
   *
   * The catch below falls back to the empty payload so the screen has something to render, and that made
   * the two indistinguishable to every consumer: a failure came out as `data = empty, isLoading = false`,
   * exactly like a period nobody bought in. The toast is the only other signal and it is transient, so a
   * producer who blinked read a broken request as a real zero. Callers surface it instead.
   */
  const [hasFailed, setHasFailed] = useState(false);

  // Serialised, so an object literal rebuilt on every render does not refire the request forever.
  const extraKey = JSON.stringify(extraPayload);

  useEffect(() => {
    let active = true;

    if (!filters.botConfigIds?.length) {
      setData(empty);
      setHasFailed(false);
      setIsLoading(false);
      return () => {
        active = false;
      };
    }

    const parsed = parseFilters(filters);

    setIsLoading(true);
    request({
      botConfigIds: parsed.botConfigIds,
      clientTz: parsed.clientTz,
      userRoles: parsed.userRoles,
      startDate: parsed.startDate,
      endDate: parsed.endDate,
      timeAggregationPeriod,
      ...JSON.parse(extraKey),
    })
      .then((response) => {
        if (!active) return;

        setHasFailed(false);
        setData(normalise((response.data ?? empty) as Partial<T>));
      })
      .catch((error) => {
        if (!active) return;

        setData(empty);
        setHasFailed(true);
        toast.handleError(error, 'Não foi possível carregar os registros');
      })
      .finally(() => {
        if (active) setIsLoading(false);
      });

    return () => {
      active = false;
    };
  }, [filters, timeAggregationPeriod, extraKey]);

  return { data, isLoading, hasFailed };
}
